import React from 'react';
import { ChevronLeftIcon, ClockIcon, UserIcon, EyeIcon, PaperclipIcon, TagIcon, DownloadIcon, MessageSquareIcon, FileIcon } from 'lucide-react';
import { Note, NoteHistoryEntry } from './NotesManagement';
import { StatusBadge } from '../../StatusBadge';
interface NoteDetailProps {
  note: Note;
  history: NoteHistoryEntry[];
  onBack: () => void;
  locale: string;
}
export function NoteDetail({
  note,
  history,
  onBack,
  locale
}: NoteDetailProps) {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Render visibility badge
  const renderVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'Public':
        return <StatusBadge status="success" text="Public" data-id="element-16742" />;
      case 'Role-based':
        return <StatusBadge status="warning" text="Role-based" data-id="element-16743" />;
      case 'User-specific':
        return <StatusBadge status="info" text="User-specific" data-id="element-16744" />;
      case 'Creator-only':
        return <StatusBadge status="error" text="Private" data-id="element-16745" />;
      default:
        return <StatusBadge status="default" text={visibility} data-id="element-16746" />;
    }
  };
  // Render history action badge
  const renderActionBadge = (action: string) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-16747" />;
      case 'Modified':
        return <StatusBadge status="warning" text="Modified" data-id="element-16748" />;
      case 'Visibility Changed':
        return <StatusBadge status="info" text="Visibility Changed" data-id="element-16749" />;
      case 'Attachment Added':
        return <StatusBadge status="success" text="Attachment Added" data-id="element-16750" />;
      case 'Attachment Removed':
        return <StatusBadge status="error" text="Attachment Removed" data-id="element-16751" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-16752" />;
    }
  };
  return <div className="space-y-6" data-id="element-16753">
      {/* Back button */}
      <div data-id="element-16754">
        <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16755">
          <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-16756" />
          Back to Notes
        </button>
      </div>
      {/* Note details card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16757">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-16758">
          <div data-id="element-16759">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16760">
              Note Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16761">
              Full information and history for this note.
            </p>
          </div>
          <div className="flex items-center" data-id="element-16762">
            <span className="mr-2 text-sm text-gray-500" data-id="element-16763">
              {formatTimestamp(note.createdAt)}
            </span>
            {renderVisibilityBadge(note.visibility)}
          </div>
        </div>
        {/* Module reference */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-16764">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-16765">
            <div className="sm:col-span-1" data-id="element-16766">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16767">
                Module Reference
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-16768">
                <span className="font-medium text-indigo-600" data-id="element-16769">
                  {note.moduleType} #{note.moduleId}
                </span>
                <span className="block text-gray-500" data-id="element-16770">{note.moduleName}</span>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16771">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16772">Note Type</dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-16773">
                {note.noteTypeName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16774">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16775">Author</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-16776">
                <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-16777" />
                {note.authorName}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16778">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16779">Visibility</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-16780">
                <EyeIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-16781" />
                {note.visibility}
                {note.visibilityDetails && <span className="ml-1 text-gray-500" data-id="element-16782">
                    ({note.visibilityDetails})
                  </span>}
              </dd>
            </div>
            {(note.entityName || note.siteName) && <div className="sm:col-span-2" data-id="element-16783">
                <dt className="text-sm font-medium text-gray-500" data-id="element-16784">Location</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-16785">
                  {note.entityName && <span className="mr-2" data-id="element-16786">Entity: {note.entityName}</span>}
                  {note.siteName && <span data-id="element-16787">Site: {note.siteName}</span>}
                </dd>
              </div>}
            <div className="sm:col-span-2" data-id="element-16788">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16789">
                Note Content
              </dt>
              <dd className="mt-1 text-sm text-gray-900 p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap" data-id="element-16790">
                {note.body}
              </dd>
            </div>
            {note.tags && note.tags.length > 0 && <div className="sm:col-span-2" data-id="element-16791">
                <dt className="text-sm font-medium text-gray-500" data-id="element-16792">Tags</dt>
                <dd className="mt-1" data-id="element-16793">
                  <div className="flex flex-wrap gap-2" data-id="element-16794">
                    {note.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-16795">
                        <TagIcon className="h-3 w-3 mr-1" data-id="element-16796" />
                        {tag}
                      </span>)}
                  </div>
                </dd>
              </div>}
            {note.hasAttachment && <div className="sm:col-span-2" data-id="element-16797">
                <dt className="text-sm font-medium text-gray-500" data-id="element-16798">
                  Attachment
                </dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-16799">
                  <div className="flex items-center" data-id="element-16800">
                    <FileIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-16801" />
                    <span data-id="element-16802">{note.attachmentName}</span>
                    <button className="ml-2 inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-16803">
                      <DownloadIcon className="h-3 w-3 mr-1" data-id="element-16804" />
                      Download
                    </button>
                  </div>
                </dd>
              </div>}
          </dl>
        </div>
      </div>
      {/* Note history */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16805">
        <div className="px-4 py-5 sm:px-6" data-id="element-16806">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16807">
            Note History
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16808">
            Audit trail of changes to this note.
          </p>
        </div>
        <div className="border-t border-gray-200" data-id="element-16809">
          <ul className="divide-y divide-gray-200" data-id="element-16810">
            {history.length === 0 ? <li className="px-4 py-4 sm:px-6 text-center text-sm text-gray-500" data-id="element-16811">
                No history available for this note.
              </li> : history.map(entry => <li key={entry.id} className="px-4 py-4 sm:px-6" data-id="element-16812">
                  <div className="flex items-center justify-between" data-id="element-16813">
                    <div className="flex items-center" data-id="element-16814">
                      <ClockIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-16815" />
                      <div data-id="element-16816">
                        <div className="flex items-center" data-id="element-16817">
                          {renderActionBadge(entry.action)}
                          <span className="ml-2 text-sm text-gray-500" data-id="element-16818">
                            {formatTimestamp(entry.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-16819">
                          By: {entry.userName}
                        </div>
                        {entry.details && <div className="mt-1 text-sm text-gray-500" data-id="element-16820">
                            {entry.details}
                          </div>}
                      </div>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
    </div>;
}